import { get, IHttpRequestConfig, post, put, remove } from "@/browser";
import { applyHeaders, extractDataFromResponse } from "@/core/api";
import { compose, IURLSearchParams } from "@/core/url";

import { ILocation } from "../locations";
import {
  consultantProfileType,
  IConsultantProfile,
  IConsultantProfileDto,
  IConsultantProfileSettings,
  IPartialPageableConsultantProfile,
  IPartialPageableShortConsultantProfile,
  pageableConsultantProfileType,
} from "./types-ensure";

const API = {
  copy: `/api/consultant-profiles/{consultantProfileId}/copy`,
  delete: `/api/consultant-profiles/{consultantProfileId}`,
  unlink: `/api/consultant-profiles/{consultantProfileId}/unlink`,
  get: `/api/consultant-profiles/{consultantProfileId}`,
  list: `/api/consultant-profiles`,
  listJRA: `/api/consultant-profiles/job-request-application`,
  listManagers: `/api/consultant-profiles/managers`,
  post: `/api/consultant-profiles`,
  put: `/api/consultant-profiles/{consultantProfileId}`,
  settings: `/api/consultant-profiles/{consultantProfileId}/settings`,
  putLocation: `/api/consultant-profiles/{consultantProfileId}/location`,
  state: `/api/consultant-profiles/{consultantProfileId}/state`,
  clearNewSuggestedDataFlag: `/api/consultant-profiles/{consultantProfileId}/clear-new-suggested-data-flag`,
};

export interface IFetchConsultantProfilesListParams extends IURLSearchParams {
  companyId?: number;
  profileIds?: number[];
}

export const fetchConsultantProfileList = (
  params: IFetchConsultantProfilesListParams = {},
  options: IHttpRequestConfig = {},
) => {
  const { companyId, ...searchParams } = params;
  const url = compose(API.list, {}, searchParams);
  return get<IPartialPageableShortConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableConsultantProfileType);
};

export interface IFetchConsultantProfilesListJRAParams extends IURLSearchParams {
  companyId?: number;
  jobRequestId: number;
  query?: string;
  queryType?: "NAME" | "FULL";
}

export const fetchConsultantProfileListJRA = (
  params: IFetchConsultantProfilesListJRAParams,
  options: IHttpRequestConfig = {},
) => {
  const { companyId, ...searchParams } = params;

  const url = compose(API.listJRA, {}, searchParams);

  return get<IPartialPageableConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableConsultantProfileType);
};

export const fetchConsultantProfileManagerList = (
  params: IFetchConsultantProfilesListParams = {},
  options: IHttpRequestConfig = {},
) => {
  const { companyId, ...searchParams } = params;
  const url = compose(API.listManagers, {}, searchParams);
  return get<IPartialPageableConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(pageableConsultantProfileType);
};

export interface IFetchConsultantProfileParams {
  companyId?: number;
  consultantProfileId: number;
}

export const fetchConsultantProfile = (params: IFetchConsultantProfileParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.get, restParams);
  return get<IConsultantProfile>(url, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(consultantProfileType);
};

export interface IPostConsultantProfileParams {}

export const postConsultantProfile = (
  _params?: IPostConsultantProfileParams,
  data: IConsultantProfileDto = {},
  options?: IHttpRequestConfig,
) => {
  return post<IConsultantProfile>(API.post, data, options).then(extractDataFromResponse).then(consultantProfileType);
};

export interface IPutConsultantProfileParams {
  companyId?: number;
  consultantProfileId: number;
}

export const putConsultantProfile = (
  params: IPutConsultantProfileParams,
  data?: IConsultantProfile,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.put, restParams);
  return put<IConsultantProfile>(url, data, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(consultantProfileType);
};

export const putConsultantProfileLocation = (
  params: IPutConsultantProfileParams,
  data: ILocation,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.putLocation, restParams);
  return put<IConsultantProfile>(url, data, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(consultantProfileType);
};

export interface ICopyConsultantProfileParams {
  consultantProfileId: number;
}

export const copyConsultantProfile = (
  params: IPutConsultantProfileParams,
  data?: IConsultantProfile,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.copy, restParams);
  return post<IConsultantProfile>(url, data, applyHeaders({ companyId }, options))
    .then(extractDataFromResponse)
    .then(consultantProfileType);
};

export interface ISettingsConsultantProfileParams {
  consultantProfileId: number;
}

export const updateConsultantProfileSettings = (
  params: ISettingsConsultantProfileParams,
  data: IConsultantProfileSettings,
  options?: IHttpRequestConfig,
) => {
  const url = compose(API.settings, params);
  return put<IConsultantProfile>(url, data, options).then(extractDataFromResponse).then(consultantProfileType);
};

export enum TUnableToDeleteConsultantProfileCodes {
  LAST_PROFILE = "LAST_PROFILE",
  COMPANY_LAST_ADMIN_PROFILE = "COMPANY_LAST_ADMIN_PROFILE",
}

export enum TAbleToDeleteConsultantProfileCodes {
  PERSONAL_PROFILE = "PERSONAL_PROFILE",
  COMPANY_CONSULTANT_PROFILE = "COMPANY_CONSULTANT_PROFILE",
  COMPANY_OWN_MANAGER_PROFILE = "COMPANY_OWN_MANAGER_PROFILE",
  COMPANY_MANAGER_PROFILE = "COMPANY_MANAGER_PROFILE",
}

export enum TPersonalConsultantProfileCodes {
  PERSONAL_PROFILE = "PERSONAL_PROFILE",
}

export type TDeleteConsultantProfileCodes =
  | keyof TUnableToDeleteConsultantProfileCodes
  | keyof TAbleToDeleteConsultantProfileCodes;

export interface IFetchConsultantProfileStatusParams {
  companyId?: number;
  consultantProfileId: number;
}

export interface IStateConsultantProfile {
  state: TDeleteConsultantProfileCodes;
}

export const fetchConsultantProfileState = (
  params: IFetchConsultantProfileStatusParams,
  options?: IHttpRequestConfig,
) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.state, restParams);
  return get<IStateConsultantProfile>(url, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export const deleteConsultantProfile = (params: IFetchConsultantProfileStatusParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.delete, restParams);
  return remove<IStateConsultantProfile>(url, {}, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export const unlinkConsultantProfile = (params: IFetchConsultantProfileStatusParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.unlink, restParams);
  return post<IStateConsultantProfile>(url, {}, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};

export interface IClearNewSuggestedDataFlagParams {
  consultantProfileId: number;
  companyId?: number;
}

export const clearNewSuggestedDataFlag = (params: IClearNewSuggestedDataFlagParams, options?: IHttpRequestConfig) => {
  const { companyId, ...restParams } = params;
  const url = compose(API.clearNewSuggestedDataFlag, restParams);
  const data = {
    profileId: params.consultantProfileId,
  };
  return put<undefined>(url, data, applyHeaders({ companyId }, options)).then(extractDataFromResponse);
};
